import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Shield, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Permanent Access Entry Point
 * 
 * This component handles permanent access links from external websites.
 * When users click the permanent link, this validates the access key
 * and redirects them to the login page.
 * 
 * URL Format: /access/{access_key}
 * 
 * This link NEVER changes and can be safely embedded on any website.
 */
export default function AccessLink() {
  const navigate = useNavigate();
  const { accessKey } = useParams();
  const [status, setStatus] = useState('validating');
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyAccess = async () => {
      if (!accessKey) {
        setStatus('error');
        setError('No access key provided');
        return;
      }

      try {
        // Verify the access key with backend
        const response = await axios.get(`${API}/auth/verify-access/${accessKey}`);
        
        if (response.data.valid) {
          setStatus('success');
          // Redirect to login page after brief success message
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 1000);
        }
      } catch (err) {
        setStatus('error');
        setError('Invalid or expired access link. Please contact support.');
      }
    };

    verifyAccess();
  }, [accessKey, navigate]);

  return (
    <div className="min-h-screen bg-[#F9F7F5] flex items-center justify-center" data-testid="access-link-page">
      <div className="text-center max-w-md px-6">
        {status === 'validating' && (
          <>
            <Loader2 className="w-12 h-12 text-[#4A3B32] animate-spin mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-[#2C2420] mb-2">
              Verifying Access...
            </h2>
            <p className="text-[#5D524D]">
              Please wait while we verify your access credentials.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-[#4A3B32]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-[#4A3B32]" />
            </div>
            <h2 className="font-heading text-2xl text-[#2C2420] mb-2">
              Access Verified
            </h2>
            <p className="text-[#5D524D]">
              Redirecting to login...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="font-heading text-2xl text-[#2C2420] mb-2">
              Access Denied
            </h2>
            <p className="text-[#5D524D] mb-4">
              {error}
            </p>
            <button
              onClick={() => navigate('/')}
              className="text-[#4A3B32] underline hover:no-underline"
            >
              Return to home page
            </button>
          </>
        )}
      </div>
    </div>
  );
}
